import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Globe,
  Palette,
  Smartphone,
  Search,
  Wrench,
  HeartHandshake,
  ArrowRight,
  Check,
  Clock,
  Users,
  Zap,
  ShieldCheck,
  MessageSquare,
  BarChart3,
  Image,
  FileText,
} from 'lucide-react';

/* ───── Ce qu'on fait ───── */
const coreServices = [
  {
    icon: Globe,
    title: 'Sites vitrine',
    desc: 'Un site professionnel qui présente votre activité, vos services et vos coordonnées. L\'essentiel pour être trouvé par vos clients.',
    for: 'Artisans, commerçants, indépendants',
  },
  {
    icon: Palette,
    title: 'Design personnalisé',
    desc: 'Chaque site est conçu sur-mesure pour refléter votre identité. Pas de template générique : votre site vous ressemble.',
    for: 'Toutes activités',
  },
  {
    icon: Smartphone,
    title: 'Responsive mobile',
    desc: 'Votre site s\'affiche parfaitement sur smartphone, tablette et ordinateur. Plus de 60% du trafic vient du mobile.',
    for: 'Indispensable',
  },
  {
    icon: Search,
    title: 'Référencement SEO',
    desc: 'Optimisation technique pour les moteurs de recherche. Votre site est pensé pour être visible sur Google dès sa mise en ligne.',
    for: 'Visibilité locale',
  },
  {
    icon: Wrench,
    title: 'Hébergement & maintenance',
    desc: 'Nous gérons la technique : hébergement rapide, mises à jour de sécurité, sauvegardes. Vous n\'avez rien à faire.',
    for: 'Inclus dans l\'abonnement',
  },
  {
    icon: HeartHandshake,
    title: 'Accompagnement humain',
    desc: 'Un interlocuteur unique qui vous guide de A à Z. On parle votre langue, pas celle des développeurs.',
    for: 'Zéro stress',
  },
];

/* ───── Ce qui est inclus dans chaque site ───── */
const included = [
  { icon: ShieldCheck, text: 'Certificat SSL (https sécurisé)' },
  { icon: Smartphone, text: 'Adapté mobile, tablette et PC' },
  { icon: Search, text: 'Référencement technique de base' },
  { icon: MessageSquare, text: 'Formulaire de contact' },
  { icon: BarChart3, text: 'Statistiques de visite' },
  { icon: Image, text: 'Optimisation des images' },
  { icon: FileText, text: 'Mentions légales conformes' },
  { icon: Wrench, text: 'Hébergement et maintenance' },
];

/* ───── Pour qui ───── */
const targets = [
  { title: 'Artisans', examples: 'Plombiers, électriciens, peintres, menuisiers, maçons, paysagistes' },
  { title: 'Commerçants', examples: 'Boutiques, restaurants, boulangeries, salons de coiffure, garages' },
  { title: 'Indépendants', examples: 'Consultants, coachs, professions libérales, services à la personne' },
  { title: 'TPE', examples: 'Petites entreprises, créateurs d\'entreprise, micro-entrepreneurs' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const ServicesPage = () => {
  return (
    <div className="min-h-screen pt-24 overflow-x-hidden" data-testid="services-page">
      <SEO
        title="Nos services - Création de sites internet"
        description="Création de sites internet professionnels pour artisans, commerçants et TPE. Design personnalisé, responsive mobile, référencement SEO, hébergement inclus."
        path="/services"
      />

      {/* ═══════ HERO ═══════ */}
      <section className="pt-8 pb-20 px-6 lg:px-12" data-testid="services-hero">
        <div className="max-w-7xl mx-auto text-center">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="section-label mb-4">
            Nos services
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6"
            data-testid="services-title"
          >
            On crée votre site, <br className="hidden sm:block" />
            <span className="text-[#8B5A8B]">vous développez votre activité</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-base md:text-lg text-white/50 max-w-2xl mx-auto mb-10"
          >
            Des sites internet professionnels, modernes et adaptés aux mobiles.
            Pensés pour les artisans, commerçants et TPE qui veulent être visibles et inspirer confiance.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap justify-center gap-6"
          >
            {[
              { icon: Clock, text: 'En ligne sous 7 jours*' },
              { icon: Users, text: '1 interlocuteur dédié' },
              { icon: Zap, text: 'Dès 348 € TTC' },
            ].map((badge, i) => (
              <div key={i} className="flex items-center gap-2 text-white/60">
                <badge.icon size={18} className="text-[#8B5A8B]" />
                <span className="text-sm">{badge.text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════ CE QU'ON FAIT ═══════ */}
      <section className="py-24 px-6 lg:px-12" data-testid="services-core">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
            <p className="section-label mb-4">Ce que nous faisons</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">
              Tout ce qu'il faut pour <span className="text-[#8B5A8B]">votre présence en ligne</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreServices.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="glass-card rounded-2xl p-7"
                data-testid={`service-card-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#8B5A8B]/10 border border-[#8B5A8B]/20 flex items-center justify-center mb-5">
                  <s.icon className="text-[#8B5A8B]" size={22} />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{s.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed mb-4">{s.desc}</p>
                <span className="inline-block text-[#8B5A8B] text-xs font-medium bg-[#8B5A8B]/10 px-3 py-1 rounded-full">
                  {s.for}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ INCLUS DANS CHAQUE SITE ═══════ */}
      <section className="py-24 px-6 lg:px-12" data-testid="services-included">
        <div className="max-w-5xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-14">
            <p className="section-label mb-4">Inclus dans chaque site</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">
              Pas de surprise, <span className="text-[#8B5A8B]">tout est compris</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 sm:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              {included.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#8B5A8B]/10 border border-[#8B5A8B]/15 flex items-center justify-center flex-shrink-0">
                    <item.icon className="text-[#8B5A8B]" size={16} />
                  </div>
                  <span className="text-white/70 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════ POUR QUI ═══════ */}
      <section className="py-24 px-6 lg:px-12" data-testid="services-targets">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-14">
            <p className="section-label mb-4">Pour qui ?</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">
              Des sites pensés pour <span className="text-[#8B5A8B]">votre métier</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {targets.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card rounded-2xl p-7 text-center"
              >
                <h3 className="text-white font-bold text-lg mb-3">{t.title}</h3>
                <p className="text-white/40 text-sm leading-relaxed">{t.examples}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ CTA ═══════ */}
      <section className="py-24 px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto glass-card rounded-3xl p-10 md:p-14 text-center relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#8B5A8B]/10 rounded-full blur-[80px] pointer-events-none" />
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4 tracking-tight">
              Prêt à vous <span className="text-[#8B5A8B]">lancer</span> ?
            </h2>
            <p className="text-white/50 text-base md:text-lg mb-10 max-w-xl mx-auto leading-relaxed">
              Découvrez nos formules à partir de 348 € TTC ou contactez-nous pour un devis gratuit adapté à votre activité.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/tarifs"
                className="glow-btn inline-flex items-center justify-center gap-2"
                data-testid="services-cta-pricing"
              >
                Voir nos tarifs
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                className="outline-btn inline-flex items-center justify-center gap-2"
                data-testid="services-cta-contact"
              >
                Obtenir un devis gratuit
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      <p className="text-center text-white/30 text-xs pb-8">
        * Objectif de mise en ligne sous 7 jours, conditionné à la réception de vos contenus et validations.
      </p>
    </div>
  );
};

export default ServicesPage;
